import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Pagination from '../../components/common/Pagination';
import AdvancedSearch, { SearchField, SearchCondition, conditionsToQueryParams, queryParamsToConditions } from '../../components/common/AdvancedSearch';

interface User {
  id: string;
  name: string;
  email: string;
}

interface Task {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  assigneeId: string;
  assigneeName: string;
  startDate: string;
  endDate: string;
  progress: number;
  parentTaskId: string | null;
  subtasks?: Task[];
  createdAt: string;
}

type ViewMode = 'list' | 'gantt';

const categories = [
  { value: '', label: 'なし' },
  { value: 'development', label: '開発' },
  { value: 'design', label: 'デザイン' },
  { value: 'meeting', label: 'ミーティング' },
  { value: 'document', label: 'ドキュメント' },
  { value: 'review', label: 'レビュー' },
  { value: 'other', label: 'その他' },
];

const categoryColors: Record<string, string> = {
  development: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
  design: 'bg-pink-100 text-pink-800 dark:bg-pink-900/50 dark:text-pink-200',
  meeting: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
  document: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200',
  review: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200',
  other: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const initialFormData = {
  title: '',
  description: '',
  startDate: new Date().toISOString().split('T')[0],
  endDate: new Date().toISOString().split('T')[0],
  priority: 'medium',
  category: '',
  assigneeId: '',
  parentTaskId: '',
};

export default function TaskList() {
  const { canCreate } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchConditions, setSearchConditions] = useState<SearchCondition[]>(() => {
    const saved = searchParams.get('conditions');
    return saved ? queryParamsToConditions(saved) : [];
  });
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [showModal, setShowModal] = useState(false);
  
  // ページネーション・ソート・表示件数（URL同期）
  const currentPage = parseInt(searchParams.get('page') || '1');
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: searchParams.get('sortBy') || 'createdAt',
    direction: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
  });
  const setCurrentPage = (page: number | ((prev: number) => number)) => {
    const p = typeof page === 'function' ? page(currentPage) : page;
    setSearchParams(prev => { prev.set('page', String(p)); return prev; }, { replace: true });
  };
  const setPageSize = (size: number) => {
    setSearchParams(prev => { prev.set('pageSize', String(size)); prev.set('page', '1'); return prev; }, { replace: true });
  };

  // クイックアクション: ?action=new でモーダルを自動で開く（create権限チェック）
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      if (canCreate('tasks')) setShowModal(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSubtaskModal, setShowSubtaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // 詳細検索フィールド定義
  const searchFields: SearchField[] = useMemo(() => [
    { key: 'title', label: 'タイトル', type: 'text', placeholder: 'タイトルで検索' },
    { key: 'status', label: 'ステータス', type: 'select', options: [
      { value: 'todo', label: '未着手' },
      { value: 'in_progress', label: '進行中' },
      { value: 'done', label: '完了' },
    ]},
    { key: 'priority', label: '優先度', type: 'select', options: [
      { value: 'low', label: '低' },
      { value: 'medium', label: '中' },
      { value: 'high', label: '高' },
    ]},
    { key: 'category', label: 'カテゴリ', type: 'select', options: categories.filter(c => c.value).map(c => ({ value: c.value, label: c.label })) },
    { key: 'assigneeId', label: '担当者', type: 'select', options: availableUsers.map(u => ({ value: u.id, label: u.name })) },
    { key: 'startDate', label: '開始日', type: 'date' },
    { key: 'endDate', label: '終了日', type: 'date' },
    { key: 'progress', label: '進捗率', type: 'number', placeholder: '%' },
  ], [availableUsers]);

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, [searchConditions, currentPage, pageSize, sortConfig]);

  // 検索条件をURLに保存（他パラメータを維持）
  useEffect(() => {
    setSearchParams(prev => {
      if (searchConditions.length > 0) {
        prev.set('conditions', conditionsToQueryParams(searchConditions));
      } else {
        prev.delete('conditions');
      }
      prev.set('page', '1');
      return prev;
    }, { replace: true });
  }, [searchConditions, setSearchParams]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // 詳細検索条件を追加
      if (searchConditions.length > 0) {
        params.append('advancedSearch', JSON.stringify(searchConditions));
      }
      
      params.append('page', String(currentPage));
      params.append('pageSize', String(pageSize));
      params.append('sortBy', sortConfig.key);
      params.append('sortOrder', sortConfig.direction);
      
      const data = await api.get<{ items: Task[]; total: number; totalPages: number }>(`/api/tasks?${params.toString()}`);
      setTasks(Array.isArray(data?.items) ? data.items : []);
      setTotalPages(data?.totalPages ?? 1);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (conditions: SearchCondition[]) => {
    setSearchConditions(conditions);
    setCurrentPage(1);
  };

  const fetchUsers = async () => {
    try {
      const data = await api.get<{ items: User[] }>('/api/users?status=active');
      setAvailableUsers(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // 親タスクとサブタスクを整理
  const organizedTasks = useMemo(() => {
    const parentTasks = tasks.filter(t => !t.parentTaskId);
    return parentTasks.map(parent => ({
      ...parent,
      subtasks: tasks.filter(t => t.parentTaskId === parent.id),
    }));
  }, [tasks]);

  // 親タスクのみ（サブタスク作成時の選択用）
  const parentTaskOptions = useMemo(() => tasks.filter(t => !t.parentTaskId), [tasks]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || submitting) return;

    setSubmitting(true);
    try {
      await api.post('/api/tasks', formData);
      setShowModal(false);
      setShowSubtaskModal(false);
      setFormData(initialFormData);
      fetchTasks();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('タスクの作成に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (task: Task) => {
    setSelectedTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      startDate: task.startDate,
      endDate: task.endDate,
      priority: task.priority,
      category: task.category || '',
      assigneeId: task.assigneeId || '',
      parentTaskId: task.parentTaskId || '',
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTask || submitting) return;

    setSubmitting(true);
    try {
      await api.put(`/api/tasks/${selectedTask.id}`, formData);
      setShowEditModal(false);
      setSelectedTask(null);
      setFormData(initialFormData);
      fetchTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('タスクの更新に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddSubtask = (parentTask: Task) => {
    setFormData({
      ...initialFormData,
      parentTaskId: parentTask.id,
      startDate: parentTask.startDate,
      endDate: parentTask.endDate,
    });
    setShowSubtaskModal(true);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'info' | 'success'> = {
      todo: 'default',
      in_progress: 'info',
      done: 'success',
    };
    const labels: Record<string, string> = {
      todo: '未着手',
      in_progress: '進行中',
      done: '完了',
    };
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-gray-400',
      medium: 'bg-blue-400',
      high: 'bg-red-400',
    };
    return colors[priority] || 'bg-gray-400';
  };

  const handleUpdateStatus = async (taskId: string, newStatus: string) => {
    try {
      await api.put(`/api/tasks/${taskId}`, { status: newStatus });
      fetchTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleUpdateProgress = async (taskId: string, progress: number) => {
    try {
      await api.put(`/api/tasks/${taskId}`, { progress });
      fetchTasks();
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  };

  const handleDelete = (taskId: string) => {
    setDeleteTargetId(taskId);
  };

  const confirmDeleteTask = async () => {
    if (!deleteTargetId) return;
    try {
      await api.delete(`/api/tasks/${deleteTargetId}`);
      fetchTasks();
      toast.success('タスクを削除しました');
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('タスクの削除に失敗しました');
    } finally {
      setDeleteTargetId(null);
    }
  };

  const toggleExpand = (taskId: string) => {
    setExpandedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  // ガントチャート用のデータ計算
  const ganttData = useMemo(() => {
    if (tasks.length === 0) return { startDate: new Date(), endDate: new Date(), days: 0 };
    
    const allDates = tasks.flatMap(t => [new Date(t.startDate), new Date(t.endDate)]);
    const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
    
    // 前後に余裕を持たせる
    minDate.setDate(minDate.getDate() - 2);
    maxDate.setDate(maxDate.getDate() + 2);
    
    const days = Math.ceil((maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));
    
    return { startDate: minDate, endDate: maxDate, days: Math.max(days, 14) };
  }, [tasks]);

  const getGanttPosition = (task: Task) => {
    const start = new Date(task.startDate);
    const end = new Date(task.endDate);
    const totalMs = ganttData.endDate.getTime() - ganttData.startDate.getTime();
    
    const leftMs = start.getTime() - ganttData.startDate.getTime();
    const widthMs = end.getTime() - start.getTime() + (1000 * 60 * 60 * 24); // 1日分追加
    
    return {
      left: `${(leftMs / totalMs) * 100}%`,
      width: `${Math.max((widthMs / totalMs) * 100, 2)}%`,
    };
  };

  const renderForm = (onSubmit: (e: React.FormEvent) => void, isEdit = false, isSubtask = false) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <Input label="タイトル" name="title" value={formData.title} onChange={handleChange} required />
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">説明</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 text-gray-900 dark:text-white"
          rows={3}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input label="開始日" name="startDate" type="date" value={formData.startDate} onChange={handleChange} required />
        <Input label="終了日" name="endDate" type="date" value={formData.endDate} onChange={handleChange} required />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select label="優先度" name="priority" value={formData.priority} onChange={handleChange}>
          <option value="low">低</option>
          <option value="medium">中</option>
          <option value="high">高</option>
        </Select>
        <Select label="カテゴリ" name="category" value={formData.category} onChange={handleChange}>
          {categories.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </Select>
      </div>
      <Select label="担当者" name="assigneeId" value={formData.assigneeId} onChange={handleChange}>
        <option value="">未割当</option>
        {availableUsers.map(user => (
          <option key={user.id} value={user.id}>{user.name}</option>
        ))}
      </Select>
      {!isSubtask && !isEdit && (
        <Select label="親タスク（サブタスクとして作成）" name="parentTaskId" value={formData.parentTaskId} onChange={handleChange}>
          <option value="">なし（独立タスク）</option>
          {parentTaskOptions.map(task => (
            <option key={task.id} value={task.id}>{task.title}</option>
          ))}
        </Select>
      )}
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setShowModal(false);
            setShowEditModal(false);
            setShowSubtaskModal(false);
            setFormData(initialFormData);
          }}
          className="w-full sm:w-auto"
        >
          キャンセル
        </Button>
        <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
          {submitting ? '処理中...' : isEdit ? '更新' : '追加'}
        </Button>
      </div>
    </form>
  );

  const renderTaskCard = (task: Task, isSubtask = false) => (
    <div className={`${isSubtask ? 'ml-6 border-l-2 border-gray-200 dark:border-gray-700 pl-4' : ''}`}>
      <Card key={task.id} className={isSubtask ? 'bg-gray-50 dark:bg-gray-900' : ''}>
        <div className="flex items-start gap-3 sm:gap-4">
          <div className={`w-1 h-full min-h-[60px] rounded-full flex-shrink-0 ${getPriorityColor(task.priority)}`} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                {!isSubtask && task.subtasks && task.subtasks.length > 0 && (
                  <button
                    onClick={() => toggleExpand(task.id)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    <svg
                      className={`w-4 h-4 text-gray-500 transition-transform ${expandedTasks.has(task.id) ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
                <h3 className="font-semibold text-gray-900 dark:text-white">{task.title}</h3>
                {getStatusBadge(task.status)}
                {task.category && (
                  <span className={`text-xs px-2 py-0.5 rounded ${categoryColors[task.category] || categoryColors.other}`}>
                    {categories.find(c => c.value === task.category)?.label}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={task.status}
                  onChange={(e) => handleUpdateStatus(task.id, e.target.value)}
                  className="w-24 sm:w-28 text-sm"
                >
                  <option value="todo">未着手</option>
                  <option value="in_progress">進行中</option>
                  <option value="done">完了</option>
                </Select>
                {!isSubtask && (
                  <Button variant="ghost" size="sm" onClick={() => handleAddSubtask(task)}>+サブ</Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => handleEdit(task)}>編集</Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(task.id)}>削除</Button>
              </div>
            </div>
            {task.description && (
              <p className="text-gray-500 text-sm mb-2">{task.description}</p>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-500">
              <span>担当: {task.assigneeName || '未割当'}</span>
              <span className="hidden sm:inline">期間: {task.startDate} ~ {task.endDate}</span>
              <span className="sm:hidden text-xs">{task.startDate} ~ {task.endDate}</span>
              <div className="flex items-center gap-2">
                <span>進捗:</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="10"
                  value={task.progress || 0}
                  onChange={(e) => handleUpdateProgress(task.id, parseInt(e.target.value))}
                  className="w-20 sm:w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
                <span className="w-10">{task.progress || 0}%</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">タスク管理</h1>
        <div className="flex gap-2">
          {/* 表示切替 */}
          <div className="flex border dark:border-gray-700 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-sm ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
            >
              リスト
            </button>
            <button
              onClick={() => setViewMode('gantt')}
              className={`px-3 py-1.5 text-sm ${viewMode === 'gantt' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
            >
              ガント
            </button>
          </div>
          {canCreate('tasks') && (
            <Button onClick={() => setShowModal(true)} className="flex-1 sm:flex-none">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              タスクを追加
            </Button>
          )}
        </div>
      </div>

      {/* 詳細検索 */}
      <Card>
        <AdvancedSearch
          fields={searchFields}
          onSearch={handleSearch}
          quickSearchPlaceholder="タスク名で検索..."
          quickSearchField="title"
          initialConditions={searchConditions}
        />
        
        {/* 表示件数・ソート */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t dark:border-gray-700">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>表示件数:</span>
            <Select
              value={pageSize.toString()}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="w-24"
            >
              <option value="10">10</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="1000">1000</option>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>並び替え:</span>
            <Select
              value={`${sortConfig.key}-${sortConfig.direction}`}
              onChange={(e) => {
                const [key, direction] = e.target.value.split('-');
                const next = { key, direction: direction as 'asc' | 'desc' };
                setSortConfig(next);
                setSearchParams(p => { p.set('sortBy', next.key); p.set('sortOrder', next.direction); p.set('page', '1'); return p; }, { replace: true });
              }}
              className="w-40"
            >
              <option value="createdAt-desc">作成日（新しい順）</option>
              <option value="createdAt-asc">作成日（古い順）</option>
              <option value="endDate-asc">終了日（近い順）</option>
              <option value="endDate-desc">終了日（遠い順）</option>
              <option value="priority-desc">優先度（高い順）</option>
              <option value="priority-asc">優先度（低い順）</option>
              <option value="title-asc">タイトル（A-Z）</option>
            </Select>
          </div>
        </div>
      </Card>

      {/* タスク一覧 */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : tasks.length === 0 ? (
        <Card>
          <div className="text-center py-12 text-gray-500">
            タスクがありません
          </div>
        </Card>
      ) : viewMode === 'list' ? (
        <div className="space-y-3">
          {organizedTasks.map(task => (
            <div key={task.id}>
              {renderTaskCard(task)}
              {expandedTasks.has(task.id) && task.subtasks && task.subtasks.length > 0 && (
                <div className="mt-2 space-y-2">
                  {task.subtasks.map(subtask => renderTaskCard(subtask, true))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* ガントチャート表示 */
        <Card>
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              {/* 日付ヘッダー */}
              <div className="flex border-b dark:border-gray-700">
                <div className="w-48 flex-shrink-0 p-2 font-medium text-sm text-gray-700 dark:text-gray-300">
                  タスク名
                </div>
                <div className="flex-1 relative h-8">
                  {Array.from({ length: Math.ceil(ganttData.days / 7) + 1 }).map((_, i) => {
                    const date = new Date(ganttData.startDate);
                    date.setDate(date.getDate() + i * 7);
                    return (
                      <div
                        key={i}
                        className="absolute text-xs text-gray-500"
                        style={{ left: `${(i * 7 / ganttData.days) * 100}%` }}
                      >
                        {date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* タスク行 */}
              {organizedTasks.map(task => (
                <div key={task.id}>
                  <div className="flex border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                    <div className="w-48 flex-shrink-0 p-2 flex items-center gap-2">
                      {task.subtasks && task.subtasks.length > 0 && (
                        <button onClick={() => toggleExpand(task.id)} className="p-0.5">
                          <svg
                            className={`w-3 h-3 text-gray-500 transition-transform ${expandedTasks.has(task.id) ? 'rotate-90' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{task.title}</span>
                    </div>
                    <div className="flex-1 relative h-10 py-1">
                      <div
                        className={`absolute h-6 rounded ${getPriorityColor(task.priority)} opacity-80`}
                        style={getGanttPosition(task)}
                      >
                        <div
                          className="h-full bg-white/30 rounded-l"
                          style={{ width: `${task.progress || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  {expandedTasks.has(task.id) && task.subtasks?.map(subtask => (
                    <div key={subtask.id} className="flex border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                      <div className="w-48 flex-shrink-0 p-2 pl-8">
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">↳ {subtask.title}</span>
                      </div>
                      <div className="flex-1 relative h-10 py-1">
                        <div
                          className={`absolute h-5 rounded ${getPriorityColor(subtask.priority)} opacity-60`}
                          style={getGanttPosition(subtask)}
                        >
                          <div
                            className="h-full bg-white/30 rounded-l"
                            style={{ width: `${subtask.progress || 0}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* ページネーション */}
      <div className="flex justify-center">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* 作成モーダル */}
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setFormData(initialFormData); }} title="タスクを追加" size="lg">
        {renderForm(handleCreate)}
      </Modal>

      {/* 編集モーダル */}
      <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedTask(null); setFormData(initialFormData); }} title="タスクを編集" size="lg">
        {renderForm(handleUpdate, true)}
      </Modal>

      {/* サブタスク追加モーダル */}
      <Modal isOpen={showSubtaskModal} onClose={() => { setShowSubtaskModal(false); setFormData(initialFormData); }} title="サブタスクを追加" size="lg">
        {renderForm(handleCreate, false, true)}
      </Modal>

      {/* 削除確認モーダル */}
      <Modal isOpen={!!deleteTargetId} onClose={() => setDeleteTargetId(null)} title="削除確認">
        <p className="text-gray-700 dark:text-gray-300">このタスクを削除しますか？</p>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="secondary" onClick={() => setDeleteTargetId(null)}>キャンセル</Button>
          <Button variant="danger" onClick={confirmDeleteTask}>削除</Button>
        </div>
      </Modal>
    </div>
  );
}

